import { compact } from '@fxts/core';
import { CustomEventWithDetail, html, rune, View } from 'rune-ts';
import { generateSearchUrl } from '../../../../../features/Search/const/url';
import { makeSearchScreenNavigate, PostMessageNavigate } from '../../../../../shared/app/navigate';
import { typo } from '../../../../../shared/typography/typo';
import { htmlIf } from '../../../../../shared/util';
import { dataStr } from '../../../../../shared/util/dataStr';
import { ButtonClose, ButtonCloseProps } from '../../../atoms/ButtonClose/ButtonClose';
import { SearchData } from '../SearchModule';
import klass from './RecentSerachesSection.module.scss';
import { RecentSearchesSectionController } from '@services/MarppleShop/renderApp/components/cells/SearchModule/controllers/RecentSearchesSectionController.ts';

export class RecentSearchRemoveEvent extends CustomEventWithDetail<string> {}
export class RecentSearchClickEvent extends CustomEventWithDetail<string> {}

export interface RecentSearchOption {
  klass?: string;
  theme?: 'light' | 'dark';
  is_mobile?: boolean;
  link_type?: 'link' | 'event';
  button_close_props?: Partial<ButtonCloseProps>;
  makeSearchUrl?: (name: string) => string;
  makeSearchNavigate?: (url: string) => PostMessageNavigate;
}

export class RecentSearch extends View<SearchData> {
  is_open: boolean;
  private readonly buttonCloseView: ButtonClose;

  static Theme = {
    light: 'light',
    dark: 'dark',
  } as const;

  constructor(
    data: SearchData,
    private option: RecentSearchOption,
  ) {
    super(data, option);
    this.is_open = false;
    this.buttonCloseView = new ButtonClose({
      ...this.defaultButtonCloseOptions,
      ...option.button_close_props,
    });
  }

  override template() {
    return html`<span
      class="${typo('14_medium')} ${this.option.klass} ${klass.search_recent} ${klass[this.theme]} "
    >
      ${this.linkTemplate()} ${this.buttonCloseView}
    </span>`;
  }

  private linkTemplate() {
    if (this.option.link_type === 'event') {
      return html`<button type="button" class="${klass.label}">
        ${html.preventEscape(this.data.name)}
      </button>`;
    }

    return html` <a
      data-post-message="${dataStr(this.makeSearchNavigate(this.search_url))}"
      href="${this.search_url}"
      class="${klass.label}"
    >
      ${html.preventEscape(this.data.name)}
    </a>`;
  }

  override onRender() {
    this.delegate('click', ButtonClose, () => {
      this.dispatchEvent(RecentSearchRemoveEvent, {
        bubbles: true,
        detail: this.data.name,
      });
    });

    if (this.option.link_type === 'event') {
      this.delegate('click', `.${klass.label}`, (e) => {
        e.originalEvent.preventDefault();
        this.dispatchEvent(RecentSearchClickEvent, {
          bubbles: true,
          detail: this.data.name,
        });
      });
    }
  }

  private get defaultButtonCloseOptions() {
    return {
      hide: false,
    };
  }

  private get theme(): keyof typeof RecentSearch.Theme {
    return this.option.theme ?? RecentSearch.Theme.light;
  }

  private get search_url() {
    return this.option.makeSearchUrl
      ? this.option.makeSearchUrl(this.data.name)
      : generateSearchUrl(this.data.name);
  }

  private makeSearchNavigate(url: string) {
    return this.option.makeSearchNavigate
      ? this.option.makeSearchNavigate(url)
      : makeSearchScreenNavigate(url);
  }
}

export interface RecentSearchesSectionData {
  recent_searches: SearchData[];
  is_mobile?: boolean;
  klass?: string;
}

export interface RecentSearchSectionOption {
  klass?: {
    container?: string;
    hide?: string;
    searches_container?: string;
  };
  container_type?: 'nowrap' | 'wrap';
  link_type?: 'link' | 'event';
  recent_search_options?: Partial<
    Pick<
      RecentSearchOption,
      'theme' | 'button_close_props' | 'klass' | 'makeSearchUrl' | 'makeSearchNavigate'
    >
  >;
}

export class RecentSearchesSection extends View<RecentSearchesSectionData> {
  private controller: RecentSearchesSectionController;

  constructor(
    data: RecentSearchesSectionData = { recent_searches: [] },
    public readonly options: RecentSearchSectionOption = {},
    controller?: RecentSearchesSectionController,
  ) {
    super(data);
    this.controller = controller || new RecentSearchesSectionController();
  }

  override template() {
    return html`
      <div class="${klass.recent_section} ${this.options.klass?.container ?? ''}">
        <div class="${klass.header} ${typo('12_medium')}">
          <span class="${klass.title}">${ET('mps2::search::recent_search')}</span>
          <button class="${klass.remove_all} ${typo('12_medium')} ${htmlIf(klass.hidden, this.isEmpty())}">
            ${ET('mps2::common::remove_all')}
          </button>
        </div>
        <div
          class="${klass.searches_container} ${this.options.klass?.searches_container ?? ''}"
          data-type="${this.options.container_type ?? 'nowrap'}"
        >
          ${this.isEmpty()
            ? html`<span class="${typo('14_bold')} ${klass.empty}">${ET('mps2::search::recent_empty')}</span>`
            : this.data.recent_searches.map(
                (recent_search) =>
                  new RecentSearch(recent_search, {
                    klass: `${klass.recent_search} ${this.options.recent_search_options?.klass || ''}`,
                    theme: this.options.recent_search_options?.theme || this.is_mobile ? 'light' : 'dark',
                    link_type: this.options.link_type,
                    makeSearchUrl: this.options.recent_search_options?.makeSearchUrl,
                    makeSearchNavigate: this.options.recent_search_options?.makeSearchNavigate,
                    button_close_props: this.options.recent_search_options?.button_close_props,
                  }),
              )}
        </div>
      </div>
    `;
  }

  override onRender() {
    this.controller.delegateEvents(this);
  }

  show() {
    const classes = compact([klass.hidden, this.options.klass?.hide]);
    this.element().classList.remove(...classes);
  }

  hide() {
    const classes = compact([klass.hidden, this.options.klass?.hide]);
    this.element().classList.add(...classes);
  }

  setData(data: RecentSearchesSectionData) {
    this.data.recent_searches = data.recent_searches;
  }

  private isEmpty() {
    return !this.data.recent_searches.length;
  }

  private get is_mobile(): boolean {
    return !!(this.data.is_mobile ?? rune.getSharedData(this)?.is_mobile);
  }
}
